import { createContext } from 'preact';
import { useContext, useEffect, useMemo, useState, useCallback } from 'preact/hooks';
import type { ComponentChildren } from 'preact';

const KEY = 'beercup_cookie_consent';

export type Consent = { accepted: boolean; ts: string } | null;

type Ctx = {
  consent: Consent;
  accept: () => void;
  decline: () => void;
  reset: () => void;
  openLegal: (view: 'privacy' | 'terms') => void;
  closeLegal: () => void;
  legalView: 'privacy' | 'terms' | null;
};

const C = createContext<Ctx>({
  consent: null,
  accept: () => {},
  decline: () => {},
  reset: () => {},
  openLegal: () => {},
  closeLegal: () => {},
  legalView: null,
});

function read(): Consent {
  if (typeof window === 'undefined') return null;
  try {
    const v = localStorage.getItem(KEY);
    return v ? JSON.parse(v) : null;
  } catch {
    return null;
  }
}

export function ConsentProvider({ children }: { children: ComponentChildren }) {
  const [consent, setConsent] = useState<Consent>(null);
  const [legalView, setLegalView] = useState<Ctx['legalView']>(null);

  useEffect(() => {
    setConsent(read());
  }, []);

  const accept = useCallback(() => {
    const v = { accepted: true, ts: new Date().toISOString() };
    localStorage.setItem(KEY, JSON.stringify(v));
    setConsent(v);
  }, []);

  const decline = useCallback(() => {
    const v = { accepted: false, ts: new Date().toISOString() };
    localStorage.setItem(KEY, JSON.stringify(v));
    setConsent(v);
  }, []);

  const reset = useCallback(() => {
    localStorage.removeItem(KEY);
    setConsent(null);
    setLegalView(null);
  }, []);

  const openLegal = useCallback((v: 'privacy' | 'terms') => setLegalView(v), []);
  const closeLegal = useCallback(() => setLegalView(null), []);

  useEffect(() => {
    if (!legalView) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLegalView(null);
    };
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKey);
    };
  }, [legalView]);

  const value = useMemo(
    () => ({ consent, accept, decline, reset, openLegal, closeLegal, legalView }),
    [consent, accept, decline, reset, openLegal, closeLegal, legalView]
  );

  return <C.Provider value={value}>{children}</C.Provider>;
}

export function useConsent() {
  return useContext(C);
}
